import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Modal, ModalContent, ModalHeader, ModalBody, ModalFooter, Button, Input } from '@heroui/react';
import { useCreateAlias, useUpdateAlias } from '@/features/aliases/hooks';
import { aliasSchema, type AliasFormValues } from '@/features/aliases/schema';
import type { Alias } from '@/features/aliases/types';

export function AliasFormModal({ isOpen, onClose, alias }: { isOpen: boolean; onClose: () => void; alias?: Alias | null }) {
  const isEdit = !!alias;
  const create = useCreateAlias();
  const update = useUpdateAlias();
  const form = useForm<AliasFormValues>({ resolver: zodResolver(aliasSchema), defaultValues: { address: '', target: '', enabled: true } });

  useEffect(() => {
    if (alias) form.reset({ address: alias.address, target: alias.target, enabled: alias.enabled });
    else form.reset({ address: '', target: '', enabled: true });
  }, [alias]); // eslint-disable-line

  const onSubmit = form.handleSubmit(async (v) => {
    if (isEdit) await update.mutateAsync({ id: alias!.id, body: v });
    else await create.mutateAsync(v);
    onClose();
  });

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <ModalContent>
        <ModalHeader className="text-fg">{isEdit ? 'Edit alias' : 'Create alias'}</ModalHeader>
        <ModalBody>
          <form id="alias-form" onSubmit={onSubmit} className="space-y-4" noValidate>
            <Input {...form.register('address')} label="Alias address" placeholder="sales@acme.com" variant="bordered" autoFocus isReadOnly={isEdit}
              isInvalid={!!form.formState.errors.address} errorMessage={form.formState.errors.address?.message} />
            <Input {...form.register('target')} label="Delivers to" placeholder="jane@acme.com" variant="bordered"
              isInvalid={!!form.formState.errors.target} errorMessage={form.formState.errors.target?.message} />
          </form>
        </ModalBody>
        <ModalFooter>
          <Button variant="light" onPress={onClose}>Cancel</Button>
          <Button color="primary" type="submit" form="alias-form" isLoading={form.formState.isSubmitting}>{isEdit ? 'Save' : 'Create'}</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
